import isEqual from "lodash/isEqual";
import qs from "query-string";
import type {
  RecommendationDataPaginationResponse_ArtShortSerializer_,
  RecommendationPaginationResponse_ArtShortSerializer_,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

interface IRecommendQueryArgs {
  art_group: number[];
  limit?: number;
  offset?: number;
}

export const recommendationApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getRecommendation: build.query<
      RecommendationDataPaginationResponse_ArtShortSerializer_,
      IRecommendQueryArgs
    >({
      query: params => ({
        url: "/foundation/api/arts/personal-recommendations",
        params,
      }),
      transformResponse: (
        response: RecommendationPaginationResponse_ArtShortSerializer_
      ) => {
        return response.payload;
      },
      /**
       * Формируем ключ для кэша без оффсета и лимита.
       * На каждый поисковый запрос, с любым включенным фильтром будет отдельный кэш
       */
      serializeQueryArgs: ({ queryArgs }) => {
        const { limit: _l, offset: _o, ...args } = queryArgs;
        const query = Object.fromEntries(
          Object.entries(args).map(([key, value]) => {
            if (key === "") {
              return ["remove", null];
            }
            return [key, value];
          })
        );
        return qs.stringify(query, { skipNull: true });
      },
      merge: (currentCache, newResponse, { arg }) => {
        const offset = Number(arg.offset);
        /** кэшированный оффсет для след страницы из прошлого запроса */
        const cachedNextOffset = currentCache.pagination?.next_page
          ? new URLSearchParams(currentCache.pagination?.next_page).get(
              "offset"
            )
          : null;
        /** кэшированный оффсет для пред страницы из прошлого запроса */
        const cachedPrevOffset = currentCache.pagination?.previous_page
          ? new URLSearchParams(currentCache.pagination?.previous_page).get(
              "offset"
            )
          : null;
        /** проверка сейчас загрузилась след страница или нет,
         *  для этого проверяем была ли в кэше уже ссылка на след страницу и сравниваем offset-ы
         *  если офсет текущего запроса совпал или оказался больше закэшированного,
         *  значит грузится след страница
         *  */
        const isLoadingBottom =
          cachedNextOffset && offset >= Number(cachedNextOffset);
        /** проверка сейчас загрузилась пред страница или нет,
         *  для этого проверяем была ли в кэше уже ссылка на пред страницу и сравниваем offset-ы
         *  если офсет текущего запроса совпал или оказался меньше закэшированного,
         *  значит грузится пред страница
         *  */
        const isLoadingTop =
          cachedPrevOffset && offset <= Number(cachedNextOffset);

        if (
          (!cachedNextOffset && !cachedPrevOffset) ||
          (!isLoadingBottom && !isLoadingTop)
        ) {
          /** если в кэше нет данных про пред/след страницы - значит это первая загрузка
           * или если грузится офсет который попал в границы уже закэшированных страниц, считаем что эта та же сама страница,
           * в обоих случаях перетираем кэш
           */
          currentCache.data = newResponse.data;
        } else if (isLoadingBottom) {
          /** для след страницы добавляем новые арты вниз от закэшированных */
          currentCache.data.push(...newResponse.data);
        } else if (isLoadingTop) {
          /** для пред страницы добавляем новые арты сверху от закэшированных */
          currentCache.data.unshift(...newResponse.data);
        }

        currentCache.pagination = {
          ...newResponse.pagination,
          ...(isLoadingTop
            ? /** если грузится пред страница то в кэше пагинации меняется только ссылка на очередную пред
               * ссылку на след берем из кэша
               */
              { next_page: currentCache.pagination?.next_page }
            : {}),
          ...(isLoadingBottom
            ? /** если грузится след страница то в кэше пагинации меняется только ссылка на очередную след
               * ссылку на пред берем из кэша
               */
              { previous_page: currentCache.pagination?.previous_page }
            : {}),
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
  }),
});
